/**
 * Amazon Ads — Stores Analytics API Section-Level Column Map
 * (Story 19.16.1, Task 7)
 *
 * 28 metrics = 4 per-level metrics × 7 levels in the Brand Store section
 * hierarchy. Sourced from the **Stores Analytics API**, which is a SEPARATE
 * Amazon endpoint (`/stores/analytics/...`) — NOT the DSP Reports API.
 *
 * Per ppc.land + Amazon Ads What's New (May 2026): Amazon launched
 * section-level Brand Store insights with the Stores Analytics API getting
 * NEW endpoints for programmatic reporting on January 16, 2026 (metrics
 * available since January 10, 2026). 4 metrics per level — `renders`,
 * `viewableImpressions`, `clicks`, `CTR`. Available in 29 countries spanning
 * NA / SA / Europe / Middle East / Asia Pacific / Africa.
 *
 * Why a separate report-type union: STORE_ANALYTICS_SECTION_LEVEL is NOT a
 * DSP report-type — the API endpoint differs. We model it via a separate
 * `StoreAnalyticsReportTypeId` union so the discriminated-union
 * exhaustiveness in the DSP switches stays clean.
 *
 * **CORRECTION from the prior 19.16.1 draft:** the 35-cell (5×7) figure was
 * wrong; web-research delta May 2026 (ppc.land + Amazon Ads What's New +
 * andersonassociates.net) confirms exactly 4 metrics per level → 28 cells
 * total.
 */

import type { DimensionDefinition, MetricDefinition } from "../adapter";

// ============================================================================
// Report-type union (separate from DSP)
// ============================================================================

export const STORE_ANALYTICS_REPORT_TYPE_IDS = [
  "STORE_ANALYTICS_SECTION_LEVEL",
] as const;

export type StoreAnalyticsReportTypeId =
  (typeof STORE_ANALYTICS_REPORT_TYPE_IDS)[number];

// ============================================================================
// Frozen constants (Round 3 Vuln 3 — compile-time `as const`)
// ============================================================================

/** 7-level Brand Store section hierarchy. Level 1 = top-level landing page. */
export const BSE_LEVELS = [1, 2, 3, 4, 5, 6, 7] as const;

/**
 * 4 per-level metrics — corrected from prior draft's 5. Source: Amazon Ads
 * What's New (Jan 16, 2026 GA announcement) + ppc.land + andersonassociates.net
 * direct quotes. **DO NOT add a 5th metric** without re-verifying Amazon's
 * documented spec.
 */
export const BSE_PER_LEVEL_METRICS = [
  "Renders",
  "ViewableImpressions",
  "Clicks",
  "CTR",
] as const;

export type BseLevel = (typeof BSE_LEVELS)[number];
export type BsePerLevelMetric = (typeof BSE_PER_LEVEL_METRICS)[number];

// ============================================================================
// Column-map entry (mirrors DSP shape but lives on STORE_ANALYTICS report-type)
// ============================================================================

export interface StoreAnalyticsMetricColumnEntry {
  readonly key: string;
  readonly label: string;
  readonly reportTypeId: StoreAnalyticsReportTypeId;
  readonly columnName: string;
  readonly dataType: "Integer" | "Percentage";
  readonly compatibleWidgets: readonly string[];
  /** Story 31-31 — aggregation semantics, carried into the MetricDefinition projection. */
  readonly aggregationKind?: MetricDefinition["aggregationKind"];
  readonly ratioComponents?: MetricDefinition["ratioComponents"];
}

const KPI_LINE_BAR_TABLE = ["kpi", "line-chart", "bar-chart", "data-table"] as const;
const KPI_GAUGE_LINE = ["kpi", "gauge", "line-chart"] as const;

function dataTypeForBseMetric(metric: BsePerLevelMetric): "Integer" | "Percentage" {
  return metric === "CTR" ? "Percentage" : "Integer";
}

function widgetsForBseMetric(metric: BsePerLevelMetric): readonly string[] {
  return metric === "CTR" ? KPI_GAUGE_LINE : KPI_LINE_BAR_TABLE;
}

/**
 * Story 19.16.1 AC #6 / Task 7.3 — generate 28-entry column map programmatically.
 * Pattern: `bsEngagementL{N}{Metric}` for N ∈ 1..7 and Metric ∈ {Renders /
 * ViewableImpressions / Clicks / CTR}.
 */
function generateBseColumnMap(): Record<string, StoreAnalyticsMetricColumnEntry> {
  const out: Record<string, StoreAnalyticsMetricColumnEntry> = {};
  for (const level of BSE_LEVELS) {
    for (const metric of BSE_PER_LEVEL_METRICS) {
      const key = `bsEngagementL${level}${metric}`;
      out[key] = {
        key,
        label: `Brand Store Engagement L${level} ${metric}`,
        reportTypeId: "STORE_ANALYTICS_SECTION_LEVEL",
        columnName: key,
        dataType: dataTypeForBseMetric(metric),
        // Story 31-31 — Renders/ViewableImpressions/Clicks are event counts
        // (additive); CTR is clicks ÷ viewable impressions, weighted by the
        // SAME-LEVEL viewable-impressions metric.
        ...(metric === "CTR"
          ? {
              aggregationKind: "ratio" as const,
              ratioComponents: {
                numerator: `bsEngagementL${level}Clicks`,
                denominator: `bsEngagementL${level}ViewableImpressions`,
              },
            }
          : { aggregationKind: "additive" as const }),
        compatibleWidgets: [...widgetsForBseMetric(metric)],
      };
    }
  }
  return out;
}

export const STORE_ANALYTICS_SECTION_LEVEL_COLUMN_MAP: Record<
  string,
  StoreAnalyticsMetricColumnEntry
> = generateBseColumnMap();

export const BSE_COLUMN_COUNT = 28;

// ============================================================================
// Public arrays (consumed by getAvailableMetrics)
// ============================================================================

export const AMAZON_ADS_STORE_ANALYTICS_METRICS: MetricDefinition[] = Object.values(
  STORE_ANALYTICS_SECTION_LEVEL_COLUMN_MAP
).map((m) => ({
  key: m.key,
  label: m.label,
  type: m.dataType === "Percentage" ? "percentage" : "number",
  category: "Amazon Brand Stores",
  dataType: m.dataType,
  // Story 31-31 — carry aggregation metadata through the projection.
  ...(m.aggregationKind !== undefined ? { aggregationKind: m.aggregationKind } : {}),
  ...(m.ratioComponents !== undefined ? { ratioComponents: m.ratioComponents } : {}),
  compatibleWidgets: [...m.compatibleWidgets],
  apiField: m.columnName,
}));

/**
 * Stores Analytics dimensions — `sectionLevel` (1..7) + `pageId` /
 * `sectionId` (placeholders pending Amazon's full schema). At minimum we ship
 * `sectionLevel` so widgets can break out by hierarchy depth.
 */
export const AMAZON_ADS_STORE_ANALYTICS_DIMENSIONS: DimensionDefinition[] = [
  { key: "sectionLevel", label: "Section Level", type: "Structure" },
  { key: "pageId", label: "Page ID", type: "Structure" },
  { key: "sectionId", label: "Section ID", type: "Structure" },
];

// ============================================================================
// API endpoint constant (separate from DSP)
// ============================================================================

/**
 * Story 19.16.1 AC #6 — separate Amazon endpoint base. The full URL is
 * region-prefixed by the regional host from `oauth.ts`. Documented at
 * https://advertising.amazon.com/API/docs/en-us/guides/brand-store/insight-metrics
 */
export const AMAZON_ADS_STORE_ANALYTICS_ENDPOINT_PATH = "/stores/analytics";

/** Defensive 7-day chunk default for Stores Analytics. */
export const AMAZON_ADS_STORE_ANALYTICS_MAX_DAYS_PER_REPORT = 7;

// ============================================================================
// Story 35.7 AC #9 — `amazon_ads_bse_grid_audit` forensic log
// ============================================================================

/**
 * Story 35.7 AC #9 — emits with the BSE grid shape invariant.
 * Allows Grafana alerting on accidental shape regression
 * (4×7=28 → 5×7=35 or 4×8=32 etc.). Per Round 1 Privacy Officer carry-forward
 * + 19.13 AC #11 — log carries ZERO tenant/token/customer-name data.
 *
 * **Invocation contract (Code Review Round 1 — explicit-call pattern):**
 * Callers (the adapter's `validateConnection` first-call path + tests)
 * invoke this directly. The prior module-load-time auto-emit pattern
 * pollutes test stdout (Vitest workers re-instantiate the module per file)
 * AND fires on every Workers isolate cold start in production
 * (uncontrolled Grafana volume). Explicit invocation gives callers control
 * over emission frequency.
 */
export function emitBseGridAuditLog(): void {
  const event = {
    structured_log: "amazon_ads_bse_grid_audit",
    storyId: "35.7",
    levels: BSE_LEVELS.length,
    metricsPerLevel: BSE_PER_LEVEL_METRICS.length,
    totalColumns: BSE_COLUMN_COUNT,
    columnNamePattern: "bsEngagementL{1-7}{Renders|ViewableImpressions|Clicks|CTR}",
  };
  console.log(JSON.stringify(event));
}
